/**
 * Customer-portal contexts: the personal (B2C) account and each company (B2B)
 * membership a user can act as. The scope is a plain string here so the helpers
 * accept both the API `AuthContext` and the stored session's.
 */
interface ContextRef {
  scope: string;
  membershipId: string | null;
}

const CUSTOMER_SCOPES: ReadonlyArray<string> = ["CUSTOMER_B2C", "CUSTOMER_B2B"];

export const LAST_CONTEXT_STORAGE_KEY = "envirohub.customer.lastContext";

export function isCustomerScope(scope: string): boolean {
  return CUSTOMER_SCOPES.includes(scope);
}

export function customerContexts<T extends ContextRef>(contexts: readonly T[]): T[] {
  return contexts.filter((context) => isCustomerScope(context.scope));
}

/** Stable identity: B2C has no membership, each B2B membership is its own context. */
export function contextKey(context: ContextRef): string {
  return `${context.scope}:${context.membershipId ?? ""}`;
}

export function isSameContext(a: ContextRef, b: ContextRef): boolean {
  return contextKey(a) === contextKey(b);
}

/** The preferred customer context when still available, else the first one. */
export function pickContext<T extends ContextRef>(
  contexts: readonly T[],
  preferredKey: string | null,
): T | undefined {
  const customer = customerContexts(contexts);
  return customer.find((context) => contextKey(context) === preferredKey) ?? customer[0];
}

/** Storage can be missing or blocked (private mode, cleared data): never throw. */
export function readLastContextKey(): string | null {
  try {
    return window.localStorage.getItem(LAST_CONTEXT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function rememberLastContext(context: ContextRef): void {
  try {
    window.localStorage.setItem(LAST_CONTEXT_STORAGE_KEY, contextKey(context));
  } catch {
    // Best effort: without it the next login opens the first customer context.
  }
}
